import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import config from '../../../utils/config';
import { client } from '../../../utils/apolloClient';

const initialState = {
  preferences: {},
  fetchingDealerApiPreferences: false,
  selectedDealerApiPreferences: {},
  failedToFetchDealerApiPreferences: false,
  fetchingSystemInfo: false,
  dealerApiPreferences: {},
  failedToFetchSystemInfo: false,
  updatingDealerApiPreferences: false,
  updatedDealerApiPreferences: false,
  failedToUpdateDealerApiPreferences: false,
};

export const getDealerApiPreferences = createAsyncThunk(
  'getDealerApiPreferences',
  async ({ solutionId, entityIds, token }) => {
    const response = await client.authorizeRequest({
      method: 'GET',
      url: `${
        config.serviceEndpoints.mpDpMetadataUrl
      }/dealer-api-preferences?solutionId=${solutionId}&entityIds=${entityIds.join(
        ','
      )}`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  }
);

export const getSystemInfoForApis = createAsyncThunk(
  'getSystemInfoForApis',
  async ({ apis, appId, token }) => {
    const response = await client.authorizeRequest({
      method: 'POST',
      url: `${config.serviceEndpoints.mpDpMetadataUrl}/systemInfo`,
      data: {
        apis,
        appId,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  }
);

export const updateDealerApiPreferences = createAsyncThunk(
  'updateDealerApiPreferences',
  async ({ appId, dealerApiPreferences, token }) => {
    const response = await client.authorizeRequest({
      method: 'PATCH',
      url: `${config.serviceEndpoints.mpDpMetadataUrl}/dealer-api-preferences`,
      data: {
        appId,
        dealerApiPreferences,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  }
);

const dealerApiPreferencesSlice = createSlice({
  name: 'dealerApiPreferences',
  initialState,
  reducers: {
    setSubscriptionSettingsAvailableLocations: (state, action) => {
      state.availableLocations = action.payload;
    },
    setApiSubscriptionSettings: (state, action) => {
      // dealerApiPreferences object looks like this:
      // {
      //     "someOrgId": {
      //         "apiId": {
      //             "preference-name": "preference-value",
      //             "second-preference-name": "second-preference-value"
      //         },
      //         "apiId2": {
      //             "preference-name": "preference-value",
      //             "second-preference-name": "second-preference-value"
      //         }
      //     },
      //     "secondOrgId": {
      //         "apiId2": {
      //             "preference-name": "preference-value",
      //             "second-preference-name": "second-preference-value"
      //         }
      //     }
      // }
      const { orgId, apiId, settings } = action.payload;
      if (!state.preferences[orgId]) {
        state.preferences[orgId] = {};
      }
      if (!state.preferences[orgId][apiId]) {
        state.preferences[orgId][apiId] = {};
      }
      for (const [key, value] of Object.entries(settings || {})) {
        state.preferences[orgId][apiId][key] = value;
      }
    },
    setSelectedApiSubscriptionSettings: (state, action) => {
      state.preferences = action.payload;
    },
    resetDealerApiPreferencesUpdateStatus(state) {
      state.updatingDealerApiPreferences = false;
      state.updatedDealerApiPreferences = false;
      state.failedToUpdateDealerApiPreferences = false;
    }
  },
  extraReducers: {
    [getDealerApiPreferences.pending]: (state, action) => {
      state.fetchingDealerApiPreferences = true;
      state.failedToFetchDealerApiPreferences = false;
    },
    [getDealerApiPreferences.fulfilled]: (state, action) => {
      state.fetchingDealerApiPreferences = false;
      state.selectedDealerApiPreferences = action.payload.data;
      state.failedToFetchDealerApiPreferences = false;
    },
    [getDealerApiPreferences.rejected]: (state, action) => {
      state.fetchingDealerApiPreferences = false;
      state.failedToFetchDealerApiPreferences = true;
    },
    [getSystemInfoForApis.pending]: (state, action) => {
      state.fetchingSystemInfo = true;
      state.failedToFetchSystemInfo = false;
    },
    [getSystemInfoForApis.fulfilled]: (state, action) => {
      state.fetchingSystemInfo = false;
      state.dealerApiPreferences = action.payload.data.dealerApiPreferences;
      state.failedToFetchSystemInfo = false;
    },
    [getSystemInfoForApis.rejected]: (state, action) => {
      state.fetchingSystemInfo = false;
      state.failedToFetchSystemInfo = true;
    },
    [updateDealerApiPreferences.pending]: (state, action) => {
      state.updatingDealerApiPreferences = true;
      state.failedToUpdateDealerApiPreferences = false;
      state.updatedDealerApiPreferences = false;
    },
    [updateDealerApiPreferences.fulfilled]: (state, action) => {
      state.updatingDealerApiPreferences = false;
      state.failedToUpdateDealerApiPreferences = false;
      state.updatedDealerApiPreferences = true;
    },
    [updateDealerApiPreferences.rejected]: (state, action) => {
      state.updatingDealerApiPreferences = false;
      state.failedToUpdateDealerApiPreferences = true;
      state.updatedDealerApiPreferences = false;
    },
  },
});

export const {
  setApiSubscriptionSettings,
  setSubscriptionSettingsAvailableLocations,
  setSelectedApiSubscriptionSettings,
  resetDealerApiPreferencesUpdateStatus
} = dealerApiPreferencesSlice.actions;

export default dealerApiPreferencesSlice.reducer;
